<template>
  <div class="private-fund-subscribe" :class="[bgColor, getlang, { 'is-pc': !isApp }]">
    <FundNavBar v-if="!isApp" :title="$t('privateFund.subscribe.title')" />
    <div class="private-fund-subscribe__scroll">
      <div class="private-fund-subscribe__scroll-inner">
        <div class="private-fund-subscribe__content">
          <div class="private-fund-subscribe__field">
            <p class="private-fund-subscribe__label">{{ $t("privateFund.subscribe.product") }}</p>
            <p class="private-fund-subscribe__value">{{ fundName }}</p>
          </div>

          <div class="private-fund-subscribe__field private-fund-subscribe__field--account" v-if="hasAccountList">
              <div class="select-box">
                <div class="ipt-box">
                  <div class="ipt-main" @click="toggleAccountPicker">
                    <div class="val-box">
                      <span class="label private-fund-subscribe__account-label">{{ $t("privateFund.subscribe.securitiesAccount") }}</span>
                      <span v-if="account" class="val private-fund-subscribe__account-value">{{ account }}</span>
                    </div>
                    <span class="icon-arrow"></span>
                  </div>
                </div>
              </div>
          </div>

            <div
              v-else-if="accountLoadFinished"
              class="private-fund-subscribe__field private-fund-subscribe__field--account-empty"
            >
              <p class="private-fund-subscribe__label">{{ $t("privateFund.subscribe.securitiesAccount") }}</p>
              <p class="private-fund-subscribe__account-empty">{{ $t("privateFund.subscribe.accountEmpty") }}</p>
            </div>

          <div class="private-fund-subscribe__field is-amount">
            <p class="private-fund-subscribe__label">{{ $t("privateFund.subscribe.amount") }}</p>
            <div class="private-fund-subscribe__stepper">
              <button
                type="button"
                class="private-fund-subscribe__stepper-btn"
                :class="{ 'is-disabled': !canDecreaseAmount }"
                :disabled="!canDecreaseAmount"
                @click="decreaseAmount"
              >
                −
              </button>
              <div class="private-fund-subscribe__stepper-value">
                <input
                  class="private-fund-subscribe__stepper-input"
                  type="text"
                  inputmode="decimal"
                  :value="amount"
                  :placeholder="amountPlaceholder"
                  @input="handleAmountInput"
                  @blur="handleAmountBlur"
                />
              </div>
              <span class="private-fund-subscribe__stepper-currency">{{ currency }}</span>
              <button
                type="button"
                class="private-fund-subscribe__stepper-btn"
                :class="{ 'is-disabled': !canIncreaseAmount }"
                :disabled="!canIncreaseAmount"
                @click="increaseAmount"
              >
                +
              </button>
            </div>
            <p v-if="amountHintText" class="private-fund-subscribe__min-hint">
              {{ amountHintText }}
            </p>
          </div>

          <div class="private-fund-subscribe__agreement" @click="agreed = !agreed">
            <van-checkbox v-model="agreed" shape="square" icon-size="16px" @click.native.stop />
            <p class="private-fund-subscribe__agreement-text">
              {{ $t("privateFund.subscribe.agreementPrefix") }}
              <span
                v-for="(item, index) in agreementLinks"
                :key="item.type"
              >
                <span v-if="index > 0">{{
                  index === agreementLinks.length - 1
                    ? $t("privateFund.subscribe.agreementAnd")
                    : $t("privateFund.subscribe.agreementBetween")
                }}</span>
                <a
                  class="is-link"
                  :href="item.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                >{{ item.label }}</a>
              </span>
              {{ $t("privateFund.subscribe.agreementSuffix") }}
            </p>
          </div>
        </div>

        <div class="private-fund-subscribe__disclaimer">
          <DisclaimerFooterLink />
        </div>
      </div>
    </div>

    <ActionSheet
      v-if="hasAccountList"
      :title="$t('privateFund.subscribe.securitiesAccount')"
      v-model="showAccountPicker"
      get-container="body"
      :z-index="3000"
      :class="accountSheetClass"
    >
      <div>
        <div
          v-for="(item, index) in accountList"
          :key="`${item.account_type}-${item.futures_login_id}`"
          class="sheet-box"
          :class="{ getlang, 'active-border': isAccountSelected(item, index) }"
          @click="selectAccount(item, index)"
        >
          <div class="img-box">
            <img :src="item.account_type == 1 ? future : security" class="img-logo" />
            <span class="title">
              {{
                `${
                  item.account_type == 1
                    ? $t("transferInto.accountName[0]")
                    : $t("privateFund.subscribe.securitiesAccount")
                }\n&nbsp;(${item.futures_login_id})`
              }}
            </span>
          </div>
          <div class="icon-box">
            <img
              v-if="isAccountSelected(item, index)"
              :src="rightOK"
              alt=""
              class="success-icon"
            />
          </div>
        </div>
      </div>
    </ActionSheet>

    <div class="private-fund-subscribe__footer">
      <button
        type="button"
        class="private-fund-subscribe__submit"
        :class="{ 'is-disabled': !canSubmit || submitting }"
        :disabled="!canSubmit || submitting"
        @click="handleSubmit"
      >
        {{ $t("privateFund.subscribe.submit") }}
      </button>
    </div>

    <van-dialog
      v-model="showSuccessDialog"
      get-container="#app"
      :class-name="successDialogClassName"
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div class="private-fund-subscribe__success-content">
        <div class="private-fund-subscribe__success-icon">
          <van-icon name="success" />
        </div>
        <p class="private-fund-subscribe__success-text">
          {{ $t("privateFund.subscribe.submitSuccessMessage") }}
        </p>
        <button
          type="button"
          class="private-fund-subscribe__success-btn"
          @click="handleSuccessConfirm"
        >
          {{ $t("privateFund.subscribe.confirm") }}
        </button>
      </div>
    </van-dialog>

  </div>
</template>

<script>
import { ActionSheet, Icon, Checkbox, Toast, Dialog } from "vant";
import security from "@/assets/images/open-account/security.png";
import future from "@/assets/images/open-account/future.png";
import rightOK from "@/assets/images/app-pages/right_ok.png";
import { getBrowser } from "@/utils/browser";
import { getAppToken } from "@/utils/hybirdMethods";
import {
  getAgreementStorageKey,
  getPrivateFundToken,
  parseAmountValue,
  getSubscriptionAmountStep,
  isSubscriptionAmountStepValid,
  formatSubscriptionAmountValue
} from "../utils";
import FundNavBar from "../components/FundNavBar.vue";
import DisclaimerFooterLink from "../components/DisclaimerFooterLink.vue";

const browser = getBrowser();
const MOCK_ACCOUNTS = [];
const AGREEMENT_LINK_TYPES = [
  { type: "terms", labelKey: "agreementTerms" },
  { type: "memo", labelKey: "agreementMemo" },
  { type: "appendix", labelKey: "agreementAppendix" }
];

export default {
  components: {
    ActionSheet,
    [Icon.name]: Icon,
    [Checkbox.name]: Checkbox,
    [Dialog.Component.name]: Dialog.Component,
    FundNavBar,
    DisclaimerFooterLink
  },
  data() {
    return {
      security,
      future,
      rightOK,
      isApp: browser.isApp,
      fundName: "",
      currency: "",
      amount: "",
      agreed: false,
      account: "",
      accountList: MOCK_ACCOUNTS,
      selectedAccount: null,
      activeSelect: 0,
      accountLoadFinished: false,
      showAccountPicker: false,
      showSuccessDialog: false,
      agreementUrls: {},
      minSubscription: "",
      additionalSubscription: "",
      amountStep: 0,
      submitting: false
    };
  },
  computed: {
    bgColor() {
      return this.$store.getters["common/bgColor"];
    },
    successDialogClassName() {
      const classes = ["private-fund-subscribe__success-dialog"];
      if (this.bgColor === "black-theme") {
        classes.push("black-theme-dialog");
      }
      return classes.join(" ");
    },
    hasAccountList() {
      return this.accountList.length > 0;
    },
    getlang() {
      return localStorage.getItem("localLang");
    },
    canSubmit() {
      return this.isAmountValid && this.isAccountValid && this.agreed && !this.submitting;
    },
    amountPlaceholder() {
      const minAmount = parseAmountValue(this.minSubscription);
      if (minAmount == null) {
        return "";
      }
      return Number.isInteger(minAmount) ? String(minAmount) : minAmount.toFixed(2);
    },
    canDecreaseAmount() {
      const minAmount = parseAmountValue(this.minSubscription);
      const currentAmount = parseAmountValue(this.amount);
      if (minAmount == null || currentAmount == null) {
        return false;
      }
      return currentAmount > minAmount;
    },
    canIncreaseAmount() {
      return this.amountStep > 0;
    },
    minSubscriptionHint() {
      if (!this.minSubscription) {
        return "";
      }
      const text = String(this.minSubscription).trim();
      if (!this.currency) {
        return text;
      }
      if (text.toUpperCase().includes(String(this.currency).toUpperCase())) {
        return text;
      }
      return `${this.currency} ${text}`;
    },
    amountHintText() {
      const amount = parseAmountValue(this.amount);
      const minAmount = parseAmountValue(this.minSubscription);
      if (
        amount != null &&
        amount > 0 &&
        minAmount != null &&
        amount >= minAmount &&
        !isSubscriptionAmountStepValid(
          this.amount,
          this.minSubscription,
          this.additionalSubscription
        )
      ) {
        return `*${this.$t("privateFund.subscribe.stepAmountInvalid")}`;
      }
      if (!this.minSubscriptionHint) {
        return "";
      }
      return `*${this.$t("privateFund.subscribe.minAmountHint")} ${this.minSubscriptionHint}`;
    },
    isAmountValid() {
      return isSubscriptionAmountStepValid(
        this.amount,
        this.minSubscription,
        this.additionalSubscription
      );
    },
    isAccountValid() {
      return Boolean(this.account && this.account.trim());
    },
    agreementLinks() {
      return AGREEMENT_LINK_TYPES.filter(item => this.agreementUrls[item.type]).map(item => ({
        type: item.type,
        label: this.$t(`privateFund.subscribe.${item.labelKey}`),
        url: this.agreementUrls[item.type]
      }));
    },
    accountSheetClass() {
      return [
        "action-sheet-sex",
        "private-fund-subscribe__account-sheet",
        this.bgColor === "black-theme" ? "is-dark-theme" : ""
      ];
    }
  },
  created() {
    const { name, currency, minSubscription, additionalSubscription } = this.$route.query;
    this.fundName = name || "";
    this.currency = currency || "";
    this.minSubscription = minSubscription || "";
    this.additionalSubscription = additionalSubscription || "";
    this.initAmount();
    this.loadAgreementUrls();
    this.initUserInfo();
  },
  methods: {
    async initUserInfo() {
      if (browser.isApp) {
        await this.logAppUserInfo();
        return;
      }
      await this.loadPcAccountList();
    },
    async loadPcAccountList() {
      try {
        const situation = await this.$apis.accountSituationV2();
        this.applyAccountList(situation?.account_list);
      } catch (error) {
        console.log("[private-fund/subscribe] PC 获取账户信息失败:", error);
      } finally {
        this.accountLoadFinished = true;
      }
    },
    async logAppUserInfo() {
      if (!browser.isApp) {
        return;
      }
      try {
        const res = await getAppToken();
        if (res === "isGetPro") {
          console.log("[private-fund/subscribe] App 用户信息: isGetPro");
          this.accountLoadFinished = true;
          return;
        }
        const userInfo = res ? JSON.parse(res) : null;
        console.log("[private-fund/subscribe] App 用户信息:", userInfo);

        if (userInfo?.token) {
          localStorage.setItem("token", userInfo.token);
          sessionStorage.setItem("token", userInfo.token);
        } else {
          Toast(this.$t("privateFund.subscribe.tokenEmpty"));
          this.accountLoadFinished = true;
          return;
        }

        const situation = await this.$apis.accountSituationV2();
        console.log("[private-fund/subscribe] account_list:", situation?.account_list);
        this.applyAccountList(situation?.account_list);
      } catch (error) {
        console.log("[private-fund/subscribe] 获取 App 用户/账户信息失败:", error);
      } finally {
        this.accountLoadFinished = true;
      }
    },
    applyAccountList(accountList) {
      if (!Array.isArray(accountList) || !accountList.length) {
        this.accountList = [];
        this.account = "";
        this.selectedAccount = null;
        return;
      }
      const accounts = accountList
        .filter(item => !item.account_type || Number(item.account_type) === 2)
        .map(item => ({
          account_type: Number(item?.account_type) || 2,
          futures_login_id: this.getAccountId(item)
        }))
        .filter(item => item.futures_login_id);
      if (!accounts.length) {
        return;
      }
      this.accountList = accounts;
      this.selectedAccount = accounts[0];
      this.account = accounts[0].futures_login_id;
      this.activeSelect = 0;
    },
    getAccountId(item) {
      return String(item?.futures_login_id || item?.account_no || "").trim();
    },
    isAccountSelected(item, index) {
      if (index === this.activeSelect) {
        return true;
      }
      if (!this.selectedAccount) {
        return false;
      }
      return (
        item.account_type === this.selectedAccount.account_type &&
        item.futures_login_id === this.selectedAccount.futures_login_id
      );
    },
    syncActiveSelect() {
      if (!this.selectedAccount) {
        this.activeSelect = 0;
        return;
      }
      const index = this.accountList.findIndex(
        item =>
          item.account_type === this.selectedAccount.account_type &&
          item.futures_login_id === this.selectedAccount.futures_login_id
      );
      this.activeSelect = index >= 0 ? index : 0;
    },
    initAmount() {
      const minAmount = parseAmountValue(this.minSubscription);
      this.amountStep = getSubscriptionAmountStep(
        this.minSubscription,
        this.additionalSubscription
      );
      if (minAmount != null) {
        this.amount = formatSubscriptionAmountValue(minAmount);
        return;
      }
      this.amount = this.amountStep > 0 ? formatSubscriptionAmountValue(this.amountStep) : "";
    },
    decreaseAmount() {
      if (!this.canDecreaseAmount) {
        return;
      }
      const currentAmount = parseAmountValue(this.amount) || 0;
      const minAmount = parseAmountValue(this.minSubscription) || 0;
      const step = this.amountStep > 0 ? this.amountStep : minAmount;
      let nextAmount = currentAmount - step;
      if (nextAmount < minAmount) {
        nextAmount = minAmount;
      }
      this.amount = formatSubscriptionAmountValue(nextAmount);
    },
    increaseAmount() {
      if (!this.canIncreaseAmount) {
        return;
      }
      const currentAmount = parseAmountValue(this.amount) || 0;
      this.amount = formatSubscriptionAmountValue(currentAmount + this.amountStep);
    },
    handleAmountInput(event) {
      const input = event.target;
      let value = String(input.value || "").replace(/[^\d.]/g, "");
      const dotIndex = value.indexOf(".");
      if (dotIndex !== -1) {
        const integerPart = value.slice(0, dotIndex);
        const decimalPart = value
          .slice(dotIndex + 1)
          .replace(/\./g, "")
          .slice(0, 2);
        value = `${integerPart}.${decimalPart}`;
      }

      if (!/^\d*(\.\d{0,2})?$/.test(value)) {
        input.value = this.amount;
        return;
      }

      this.amount = value;
      if (input.value !== value) {
        input.value = value;
      }
    },
    handleAmountBlur() {
      const raw = String(this.amount).trim();
      if (!raw) {
        return;
      }
      const num = parseAmountValue(raw);
      if (num == null || num <= 0) {
        this.amount = "";
        return;
      }
      this.amount = formatSubscriptionAmountValue(num);
    },
    isBelowMinSubscription() {
      const minAmount = parseAmountValue(this.minSubscription);
      if (minAmount == null) {
        return false;
      }
      const amount = parseAmountValue(this.amount);
      return amount != null && amount < minAmount;
    },
    handleBack() {
      this.$router.go(-1);
    },
    toggleAccountPicker() {
      if (!this.hasAccountList) {
        return;
      }
      if (!this.showAccountPicker) {
        this.syncActiveSelect();
      }
      this.showAccountPicker = !this.showAccountPicker;
    },
    selectAccount(item, index) {
      this.selectedAccount = item;
      this.account = item.futures_login_id;
      this.activeSelect = index;
      this.showAccountPicker = false;
    },
    loadAgreementUrls() {
      const fundId = this.$route.query.id;
      if (!fundId) {
        return;
      }
      try {
        const cached = sessionStorage.getItem(getAgreementStorageKey(fundId));
        if (cached) {
          this.agreementUrls = JSON.parse(cached);
        }
      } catch (error) {
        console.log("[private-fund/subscribe] 读取协议 PDF 缓存失败:", error);
      }
    },
    async ensureSubmitToken() {
      if (getPrivateFundToken()) {
        return true;
      }
      if (!browser.isApp) {
        return false;
      }
      try {
        const res = await getAppToken();
        if (res === "isGetPro" || !res) {
          return false;
        }
        const userInfo = JSON.parse(res);
        return Boolean(userInfo?.token);
      } catch (error) {
        console.log("[private-fund/subscribe] 提交前获取 token 失败:", error);
        return false;
      }
    },
    async handleSubmit() {
      if (this.submitting) {
        return;
      }
      if (!this.isAccountValid) {
        Toast(
          this.$t(
            this.hasAccountList
              ? "privateFund.subscribe.accountInvalid"
              : "privateFund.subscribe.accountEmpty"
          )
        );
        return;
      }
      if (!this.isAmountValid) {
        const amount = parseAmountValue(this.amount);
        if (amount == null || amount <= 0) {
          Toast(this.$t("privateFund.subscribe.amountInvalid"));
        } else if (this.isBelowMinSubscription()) {
          Toast(this.$t("privateFund.subscribe.minAmountInvalid"));
        } else {
          Toast(this.$t("privateFund.subscribe.stepAmountInvalid"));
        }
        return;
      }
      if (!this.agreed) {
        return;
      }
      if (!this.$route.query.id) {
        Toast(this.$t("privateFund.subscribe.fundIdEmpty"));
        return;
      }
      if (!(await this.ensureSubmitToken())) {
        Toast(this.$t("privateFund.subscribe.tokenEmpty"));
        return;
      }
      this.submitting = true;
      try {
        await this.$apis.subscribeFundProduct({
          fund_product_id: this.$route.query.id,
          futures_login_id: this.account.trim(),
          subscription_amount: this.amount,
          agree_terms: 1
        });
        this.showSuccessDialog = true;
      } catch (error) {
        console.log("[private-fund/subscribe] 提交认购失败:", error);
      } finally {
        this.submitting = false;
      }
    },
    handleSuccessConfirm() {
      this.showSuccessDialog = false;
      this.toFundOrder();
    },
    toFundOrder() {
      this.$router.replace({
        path: "/private-fund/fundOrder",
        query: {
          ...this.$route.query
        }
      });
    }
  }
};
</script>

<style scoped lang="less">
@import "../../app-pages/styles/styles.less";

.private-fund-subscribe {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: #fff;

  &.is-pc {
    padding-top: 100px;
    box-sizing: border-box;
  }

  &__scroll {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: calc(24px + env(safe-area-inset-top)) 24px 0;
  }

  &__scroll-inner {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    box-sizing: border-box;
  }

  &__content {
    flex-shrink: 0;
  }

  &__disclaimer {
    flex-shrink: 0;
    margin-top: auto;
    padding-bottom: 8px;

    /deep/ .private-fund-disclaimer-link {
      padding: 20px 0 12px;
    }
  }

  &__field {
    margin-bottom: 24px;
    padding: 24px 32px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
  }

  &__field--account {
    padding: 0;
    overflow: hidden;

    /deep/ .select-box,
    /deep/ .ipt-box {
      margin-bottom: 0;
    }

    /deep/ .ipt-main {
      padding: 24px 32px;

      &:after {
        display: none;
      }
    }

    /deep/ .val-box {
      .label {
        padding: 0 0 8px;
        color: #374151;
        font-size: 24px;
        line-height: 34px;
        animation: none;
        transform: none;
      }

      .val {
        color: #030213;
        font-size: 28px;
        font-weight: 700;
        line-height: 40px;
      }
    }
  }

  &__account-label {
    color: #374151 !important;
    font-weight: 400 !important;
  }

  &__account-value {
    color: #030213 !important;
    font-size: 28px !important;
    font-weight: 700 !important;
    line-height: 40px !important;
  }

  &__field.is-amount {
    .private-fund-subscribe__label {
      margin-bottom: 16px;
    }
  }

  &__label {
    color: #374151;
    font-size: 24px;
    line-height: 34px;
  }

  &__value {
    margin-top: 8px;
    color: #111827;
    font-size: 28px;
    font-weight: 600;
    line-height: 40px;
    word-break: break-word;
  }

  &__account-empty {
    margin-top: 12px;
    color: #e83324;
    font-size: 28px;
    line-height: 40px;
  }

  &__field--account-empty {
    margin-bottom: 24px;
  }

  &__stepper {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  &__stepper-btn {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    padding: 0;
    border: 1px solid #d1d5db;
    border-radius: 50%;
    background: #fff;
    color: #4b5563;
    font-size: 32px;
    font-weight: 400;
    line-height: 1;
    cursor: pointer;

    &.is-disabled,
    &:disabled {
      color: #d1d5db;
      border-color: #e5e7eb;
      background: #fff;
      cursor: not-allowed;
    }
  }

  &__stepper-value {
    flex: 1;
    min-width: 0;
    text-align: center;
  }

  &__stepper-input {
    width: 100%;
    padding: 0;
    border: none;
    background: transparent;
    color: #111827;
    font-size: 28px;
    font-weight: 600;
    line-height: 40px;
    text-align: center;
    outline: none;

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }
  }

  &__stepper-currency {
    flex-shrink: 0;
    color: #9ca3af;
    font-size: 28px;
    line-height: 40px;
  }

  &__min-hint {
    margin-top: 16px;
    color: #e53e3e;
    font-size: 24px;
    line-height: 34px;
  }

  &__agreement {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-top: 8px;
    overflow: hidden;

    /deep/ .van-checkbox__icon {
      border-radius: 6px;

      .van-icon {
        border-radius: 6px;
      }

      &.van-checkbox__icon--checked {
        .van-icon,
        .van-icon.van-icon-success {
          background-color: #1677ff !important;
          border-color: #1677ff !important;
          color: #fff !important;
        }
      }
    }
  }

  &__agreement-text {
    flex: 1;
    min-width: 0;
    color: #666;
    font-size: 24px;
    line-height: 38px;

    .is-link {
      color: #1677ff;
      text-decoration: none;
    }
  }

  &__footer {
    flex-shrink: 0;
    padding: 20px 24px calc(20px + env(safe-area-inset-bottom));
    background: #fff;
    box-shadow: 0 -4px 20px rgba(15, 23, 42, 0.06);
  }

  &__submit {
    width: 100%;
    height: 88px;
    border: none;
    border-radius: 44px;
    background: #171832;
    color: #fff;
    font-size: 30px;
    font-weight: 500;
    line-height: 88px;

    &.is-disabled {
      background: #d9d9d9;
      color: #fff;
    }
  }

  &__success-dialog {
    width: 620px;
    border-radius: 32px;
    overflow: hidden;

    /deep/ .van-dialog__content {
      padding: 0;
    }
  }

  &__success-content {
    padding: 56px 40px 40px;
    text-align: center;
  }

  &__success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 98px;
    height: 98px;
    border-radius: 50%;
    background: #07c160;
    color: #fff;
    font-size: 52px;
  }

  &__success-text {
    margin-top: 32px;
    color: #171717;
    font-size: 30px;
    line-height: 44px;
    text-align: center;
  }

  &__success-btn {
    width: 100%;
    height: 88px;
    margin-top: 40px;
    border: none;
    border-radius: 44px;
    background: #171832;
    color: #fff;
    font-size: 30px;
    font-weight: 500;
    line-height: 88px;
  }

  &.black-theme {
    background: #0a0b0f;

    .private-fund-subscribe__field {
      background: #1b1c24;
      border-color: #323442;
    }

    .private-fund-subscribe__label {
      color: #9d9d9f;
    }

    .private-fund-subscribe__field--account {
      /deep/ .val-box {
        .label {
          color: #9d9d9f;
        }

        .val {
          color: #fff;
          font-weight: 700;
        }
      }

      /deep/ .icon-arrow {
        opacity: 0.7;
        filter: brightness(2);
      }
    }

    .private-fund-subscribe__account-label {
      color: #9d9d9f !important;
    }

    .private-fund-subscribe__account-value {
      color: #fff !important;
      font-weight: 700 !important;
    }

    .private-fund-subscribe__value,
    .private-fund-subscribe__stepper-input {
      color: #fff;
    }

    .private-fund-subscribe__stepper-input::placeholder {
      color: #9d9d9f;
    }

    .private-fund-subscribe__stepper-currency {
      color: #9d9d9f;
    }

    .private-fund-subscribe__stepper-btn {
      background: #1b1c24;
      border-color: #505264;
      color: #fff;

      &.is-disabled,
      &:disabled {
        color: #505264;
        border-color: #323442;
        background: #1b1c24;
      }
    }

    .private-fund-subscribe__min-hint {
      color: #e53e3e;
    }

    .private-fund-subscribe__account-empty {
      color: #e83324;
    }

    .private-fund-subscribe__agreement-text {
      color: #9d9d9f;

      .is-link {
        color: #377cfb;
      }
    }

    /deep/ .van-checkbox__icon {
      .van-icon {
        border-color: #505264;
      }

      &.van-checkbox__icon--checked {
        .van-icon,
        .van-icon.van-icon-success {
          background-color: #377cfb !important;
          border-color: #377cfb !important;
          color: #fff !important;
        }
      }
    }

    .private-fund-subscribe__footer {
      background: #1b1c24;
      box-shadow: none;
    }

    .private-fund-subscribe__submit {
      background: #377cfb;

      &.is-disabled {
        background: #505264;
        color: #9d9d9f;
      }
    }

  }
}
</style>

<style lang="less">
.private-fund-subscribe__success-dialog.black-theme-dialog {
  background-color: #1b1c24;

  .van-dialog__content {
    background-color: #1b1c24;
  }

  .private-fund-subscribe__success-content {
    background: #1b1c24;
  }

  .private-fund-subscribe__success-text {
    color: #fff;
  }

  .private-fund-subscribe__success-btn {
    background: #377cfb;
  }
}

.private-fund-subscribe__account-sheet {
  .sheet-box {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 120px;
    background-color: #f9f9f9;
    border-radius: 72px;
    width: 87%;
    margin: 32px auto;

    .img-box {
      display: flex;
      align-items: center;

      .img-logo {
        width: 36px;
        height: 36px;
        object-fit: contain;
        margin-right: 8px;
        margin-left: 74px;
      }

      .title {
        font-size: 28px;
        font-weight: 500;
        color: #17161c;
        line-height: 32px;
        padding-top: 2px;
        white-space: pre-line;
      }
    }

    .icon-box {
      flex: 1;
      text-align: right;
      padding-right: 22px;
      padding-top: 10px;
      line-height: 0;
    }

    &.active-border {
      border: 0.5px solid #f1f1f1;
    }
  }

  .success-icon {
    width: 44px;
    height: 44px;
    object-fit: contain;
    vertical-align: middle;
  }
}

.private-fund-subscribe__account-sheet.is-dark-theme {
  background-color: #1b1c24;

  .van-action-sheet__header {
    background-color: #1b1c24;
    color: #fff;
  }

  .van-action-sheet__content {
    background-color: #1b1c24;
  }

  .van-action-sheet__gap {
    background-color: #1b1c24;
  }

  .van-action-sheet__close {
    color: #fff;
  }

  .sheet-box {
    background: #0a0b0f;

    .img-box .title {
      color: #fff;
    }

    &.active-border {
      border-color: #323442;
    }
  }
}

.en .private-fund-subscribe__account-sheet .sheet-box .img-box .title {
  white-space: break-spaces;
  word-break: break-word;
}
</style>
